import {
  type PortfolioDbSyncSchedulerScope,
  canSchedulePortfolioDbBackup,
  createPortfolioDbSyncExecutionState,
  dbSyncPolicyCheckIntervalMs,
  getPortfolioDbSyncSchedulerScope,
  lastUsedHeartbeatIntervalMs,
  runPortfolioDbSyncSchedulerTick,
} from '@/features/portfolio/logic/dbSyncPolicy'
import { runDbSyncTelemetryOperation } from '@/features/portfolio/logic/dbSyncTelemetry'
import { syncDbFileToRemote } from '@/features/portfolio/logic/syncDbFileToRemote'
import { updateLastUsedAt } from '@/features/portfolio/logic/updateLastUsedAt'
import { activeWriteOperationAtom } from '@/features/portfolio/states/writeOperation'
import {
  existProgressInLocalStorage,
  getPortfolioDbDirtySinceFromLocalStorage,
  getPortfolioFromLocalStorage,
  getPortfolioIsWritableFromLocalStorage,
  getPortfolioUpdatedAtFromLocalStorage,
  setPortfolioDbDirtySinceToLocalStorage,
} from '@/localStorage'
import { useAuth } from '@clerk/clerk-react'
import { captureException } from '@sentry/react'
import { getDefaultStore } from 'jotai'
import { type ReactNode, useEffect, useRef } from 'react'

/** activity listener の passive option。 */
const passiveActivityListenerOptions = { passive: true } as const

/**
 * writer heartbeat と idle-aware DB backup scheduler を application 全体へ提供する。
 * @param props child component を受け取る。
 * @returns child component をそのまま返す。
 */
const SyncProvider = ({ children }: { readonly children: ReactNode }) => {
  const { getToken } = useAuth()
  const lastUserActivityAtRef = useRef(Date.now())
  const schedulerScopeRef = useRef<PortfolioDbSyncSchedulerScope | undefined>(
    undefined,
  )
  const schedulerExecutionStateRef = useRef(
    createPortfolioDbSyncExecutionState(),
  )

  useEffect(() => {
    const recordUserActivity = (): void => {
      lastUserActivityAtRef.current = Date.now()
    }

    document.addEventListener('pointerdown', recordUserActivity)
    document.addEventListener('keydown', recordUserActivity)
    document.addEventListener(
      'wheel',
      recordUserActivity,
      passiveActivityListenerOptions,
    )
    document.addEventListener(
      'touchstart',
      recordUserActivity,
      passiveActivityListenerOptions,
    )
    document.addEventListener(
      'scroll',
      recordUserActivity,
      passiveActivityListenerOptions,
    )

    return () => {
      document.removeEventListener('pointerdown', recordUserActivity)
      document.removeEventListener('keydown', recordUserActivity)
      document.removeEventListener('wheel', recordUserActivity)
      document.removeEventListener('touchstart', recordUserActivity)
      document.removeEventListener('scroll', recordUserActivity)
    }
  }, [])

  useEffect(() => {
    let runningUpdateLastUsedAt = false
    let mounted = true
    const intervalId = window.setInterval(() => {
      if (!mounted || runningUpdateLastUsedAt) {
        return
      }

      const isWritable = getPortfolioIsWritableFromLocalStorage()
      const portfolio = getPortfolioFromLocalStorage()
      if (
        portfolio === undefined ||
        !canSchedulePortfolioDbBackup({ isWritable, plan: portfolio.plan })
      ) {
        return
      }

      runningUpdateLastUsedAt = true
      void updateLastUsedAt({ portfolio, getToken }).finally(() => {
        runningUpdateLastUsedAt = false
      })
    }, lastUsedHeartbeatIntervalMs)

    return () => {
      mounted = false
      window.clearInterval(intervalId)
    }
  }, [getToken])

  useEffect(() => {
    let mounted = true
    const evaluateDbBackupPolicy = async (): Promise<void> => {
      if (!mounted) {
        return
      }

      const isWritable = getPortfolioIsWritableFromLocalStorage()
      const portfolio = getPortfolioFromLocalStorage()
      if (
        portfolio === undefined ||
        !canSchedulePortfolioDbBackup({ isWritable, plan: portfolio.plan })
      ) {
        return
      }

      const now = Date.now()
      const localUpdatedAt = getPortfolioUpdatedAtFromLocalStorage() ?? 0
      let dirtySince = getPortfolioDbDirtySinceFromLocalStorage({
        portfolioId: portfolio.uuid,
      })
      if (localUpdatedAt > portfolio.updatedAt && dirtySince === undefined) {
        dirtySince = Math.min(localUpdatedAt, now)
        setPortfolioDbDirtySinceToLocalStorage({
          portfolioId: portfolio.uuid,
          dirtySince,
        })
      }

      const schedulerScope = getPortfolioDbSyncSchedulerScope({
        scope: schedulerScopeRef.current,
        portfolioId: portfolio.uuid,
      })
      schedulerScopeRef.current = schedulerScope

      const result = await runPortfolioDbSyncSchedulerTick({
        state: schedulerScope.state,
        executionState: schedulerExecutionStateRef.current,
        policyInput: {
          now,
          localUpdatedAt,
          remoteUpdatedAt: portfolio.updatedAt,
          dirtySince,
          lastUserActivityAt: lastUserActivityAtRef.current,
          hasActiveWriteOperation:
            getDefaultStore().get(activeWriteOperationAtom) !== null,
          hasProgress: existProgressInLocalStorage(),
        },
        startSync: ({ reason }) =>
          runDbSyncTelemetryOperation({
            metadata: {
              triggerReason: reason,
              dirtyAgeMs:
                dirtySince === undefined ? 0 : Math.max(0, now - dirtySince),
              lastWriteAgeMs: Math.max(0, now - localUpdatedAt),
              userIdleAgeMs: Math.max(0, now - lastUserActivityAtRef.current),
              visibilityState: document.visibilityState,
            },
            task: ({ telemetry }) =>
              syncDbFileToRemote({ portfolio, getToken, telemetry }),
          }),
      })

      if (result.status === 'failed') {
        captureException(result.error)
      }
    }

    const intervalId = window.setInterval(() => {
      void evaluateDbBackupPolicy()
    }, dbSyncPolicyCheckIntervalMs)

    return () => {
      mounted = false
      window.clearInterval(intervalId)
    }
  }, [getToken])

  return children
}

export default SyncProvider
